<template>
  <div
    @click="noAccess ? null : $emit('set-project', project.id)"
    class="dashboard-card project-card d-flex flex-column hover-container"
    :class="{noAccess: noAccess}"
  >
    <div class="lock-info" v-if="noAccess">
      <span class="lock-icon">
        <v-icon >mdi-lock-outline</v-icon>
      </span>
      <span class="lock-text">
        You have more than 3 projects. To access this project <RouterLink to="/subscription/my-plan">upgrade to pro</RouterLink> or delete one of your projects.
      </span>
    </div>
    <div class="d-flex justify-space-between">
      <v-icon class="mb-4">mds:grid-view</v-icon>
      <v-menu v-if="!disabled">
        <template #activator="{ on, props }">
          <v-btn
            v-bind="props"
            variant="text"
            size="small"
            icon="mds:more_vert"
            v-on="{ ...on }"
            @click.prevent
          />
        </template>

        <v-list density="compact">
          <v-tooltip location="bottom" :disabled="isProjectOwner">
            <template v-slot:activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item
                  @click="$emit('edit-project', project)"
                  prepend-icon="mdi-pencil"
                  title="Edit"
                  :disabled="!isProjectOwner"
                />
              </div>
            </template>
            <span>Only the project owner can edit the project.</span>
          </v-tooltip>

          <v-tooltip location="bottom" :disabled="isProjectOwner">
            <template v-slot:activator="{ props: tooltipProps }">
              <div v-bind="tooltipProps">
                <v-list-item
                  @click="showDeleteProjectDialog(project)"
                  prepend-icon="mdi-delete-outline"
                  title="Delete"
                  :disabled="!isProjectOwner"
                />
              </div>
            </template>
            <span>Only the project owner can delete the project.</span>
          </v-tooltip>
        </v-list>
      </v-menu>
    </div>

    <!-- <img
      width="307"
      height="40"
      src="@/assets/workflow-placeholder.svg"
      alt="workflow placeholder"
    /> -->

    <div class="project-name">
      {{ project.name }}
    </div>
    <div class="text-sub text-truncate" v-if="project?.organisation?.user">
      {{ project.organisation.name }}
    </div>

    <v-spacer/>
    <v-divider class="my-4"/>

    <div class="d-flex flex-nowrap">
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect width="20" height="20" rx="4" fill="#C3DAF6"/>
        <mask id="mask0_631_435" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="2" y="2" width="16" height="16">
          <rect x="2" y="2" width="16" height="16" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_631_435)">
          <path
            d="M12.3333 15C12.0125 15 11.7378 14.8858 11.5094 14.6573C11.2809 14.4288 11.1667 14.1542 11.1667 13.8333V13.3333H10.125C9.80417 13.3333 9.52951 13.2191 9.30104 12.9906C9.07257 12.7622 8.95833 12.4875 8.95833 12.1667V7.81944H7.91667V8.30556C7.91667 8.62639 7.80243 8.90104 7.57396 9.12951C7.34549 9.35799 7.07083 9.47222 6.75 9.47222H5.16667C4.84583 9.47222 4.57118 9.35799 4.34271 9.12951C4.11424 8.90104 4 8.62639 4 8.30556V6.16667C4 5.84583 4.11424 5.57118 4.34271 5.34271C4.57118 5.11424 4.84583 5 5.16667 5H6.75C7.07083 5 7.34549 5.11424 7.57396 5.34271C7.80243 5.57118 7.91667 5.84583 7.91667 6.16667V6.65278H11.1667V6.16667C11.1667 5.84583 11.2809 5.57118 11.5094 5.34271C11.7378 5.11424 12.0125 5 12.3333 5H13.9167C14.2375 5 14.5122 5.11424 14.7406 5.34271C14.9691 5.57118 15.0833 5.84583 15.0833 6.16667V8.30556C15.0833 8.62639 14.9691 8.90104 14.7406 9.12951C14.5122 9.35799 14.2375 9.47222 13.9167 9.47222H12.3333C12.0125 9.47222 11.7378 9.35799 11.5094 9.12951C11.2809 8.90104 11.1667 8.62639 11.1667 8.30556V7.81944H10.125V12.1667H11.1667V11.6806C11.1667 11.3597 11.2809 11.0851 11.5094 10.8566C11.7378 10.6281 12.0125 10.5139 12.3333 10.5139H13.9167C14.2375 10.5139 14.5122 10.6281 14.7406 10.8566C14.9691 11.0851 15.0833 11.3597 15.0833 11.6806V13.8333C15.0833 14.1542 14.9691 14.4288 14.7406 14.6573C14.5122 14.8858 14.2375 15 13.9167 15H12.3333ZM12.3333 8.30556H13.9167V6.16667H12.3333V8.30556ZM12.3333 13.8333H13.9167V11.6806H12.3333V13.8333ZM5.16667 8.30556H6.75V6.16667H5.16667V8.30556Z"
            fill="#363940"/>
        </g>
      </svg>
      <div class="text-sub ml-2 text-truncate">
        {{ numWorkflows }} workflows
      </div>
      <v-spacer class="mx-2"/>
      <div class="text-sub text-truncate" v-if="project?.organisation?.user">
        {{ project.organisation.user.firstname }} {{ project.organisation.user.lastname }}
      </div>
      <div v-if="project.starred" class="text-sm-right">
        <v-tooltip location="top">
          <template v-slot:activator="{ on, props }">
            <v-icon v-bind="props" v-on="{ ...on }" size="small" @click="toggleStarred()">mdi-star</v-icon>
          </template>
          <span>Starred project. Click to remove star.</span>
        </v-tooltip>
      </div>
    </div>
    <v-menu v-if="!disabled && !hideActions">
      <template #activator="{ on, props }">
        <v-btn
          v-bind="props"
          variant="plain"
          size="x-small"
          class="project-card-more-btn px-0"
          v-on="{ ...on }"
        >
          <v-icon style="font-size: 24px;">mdi-dots-horizontal</v-icon>
        </v-btn>
      </template>

      <v-list density="compact">
        <v-list-item
          @click="$emit('edit', project)"
          prepend-icon="mdi-pencil"
          title="Edit"
        />

        <v-list-item
          @click="showDeleteProjectDialog(project)"
          prepend-icon="mdi-delete-outline"
          title="Delete"
        />

        <v-list-item
          @click="toggleStarred()"
          prepend-icon="mdi-star"
          :title="project.starred ? 'Remove from starred' : 'Add to starred'"
        />

        <v-list-group
          @click.stop
        >
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              prepend-icon="mdi-content-copy"
              title="Create a copy in project:"
            />
          </template>

        </v-list-group>
      </v-list>
    </v-menu>

    <delete-confirmation-dialog
      v-model="showDeleteDialog"
      title="Confirm project deletion"
      text="Are you sure you want to delete this project and all its team members, events and cards? This operation cannot be undone."
      :confirmText="project.name"
      :onDelete="deleteProjectAction"
    />
  </div>
</template>

<script>
import projectApi from '@/api/projectApi'
import { mapState } from 'vuex'
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog'
import recentWorkflowsApi from '@/api/recentWorkflowsApi'

export default {
  components: {
    DeleteConfirmationDialog
  },
  props: {
    project: Object,
    disabled: Boolean,
    noAccess: Boolean,
    hideActions: Boolean
  },

  data () {
    return {
      processingRequest: null,
      showDeleteDialog: false,
      deleteProjectAction: null
    }
  },

  computed: {
    ...mapState({
      projectMemberships: state => state.projectMemberships.projectMemberships
    }),

    numWorkflows () {
      return this.project.workflows?.length || 0
    },

    isProjectOwner () {
      return this.project.organisation?.user?.id === this.$store.state.userId
    }
  },

  methods: {
    showDeleteProjectDialog (item) {
      this.showDeleteDialog = true
      this.deleteProjectAction = () => {
        recentWorkflowsApi.deleteProject(item.id)
        projectApi.deleteProject(item)
      }
    }
  }
}
</script>

<style scoped>
.dashboard-card {
  &.noAccess {
    position: relative;
    cursor: not-allowed;
    border-color: #363A4033;
    transition: 0.2s border-color ease-in-out;

    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background-color: #363A4033;
      transition: 0.2s background-color ease-in-out;
    }

    &:hover {
      border-color: #363A40CC;

      &::before {
        background-color: #363A40CC;
      }

      .lock-text {
        display: inline-block;
        transition: 0.2s opacity ease-in-out;
      }
    }

    .lock-text {
      display: none;
    }
  }
}

.project-name {
  color: var(--text-dark);
  font-size: 20px;
  line-height: 24px;
  letter-spacing: -0.6px;
  max-height: 50px;
  overflow: hidden;
}

.text-sub {
  color: var(--text-light);
  font-size: 12px;
  line-height: 20px;
  text-decoration-style: none !important;
}

a {
  text-decoration: none;
  color: currentColor;
}

.lock-info {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  gap: 12px;
  display: flex;
  padding: 16px 24px;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  &:hover {

  }
}

.lock-icon {
  font-size: 48px;
  color: var(--white, #ffffff);
}

.lock-text {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;
  letter-spacing: -0.03em;
  color: var(--white, #ffffff);

  a {
    text-decoration: underline;
  }
}

.dashboard-card {
  padding: 12px;
  cursor: pointer;

  width: 100%;
  overflow: hidden;
  height: 206px;

  border-radius: 6px;
  border: 1px solid var(--light-1);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);

  &.workflow-card {
    padding: 0;
  }

  &:hover {
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
  }
}
</style>
